import React, { useState, useEffect } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuditActions } from '../hooks/useAudit';
import { 
  AlertTriangle, 
  Shield, 
  Clock, 
  XCircle,
  Lock,
  Unlock
} from 'lucide-react';

interface BreakGlassAccessProps {
  onActivate?: () => void;
  onDeactivate?: () => void;
}

const BreakGlassAccess: React.FC<BreakGlassAccessProps> = ({
  onActivate,
  onDeactivate
}) => {
  const permissions = usePermissions();
  const auditActions = useAuditActions();
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [justification, setJustification] = useState('');
  const [showJustification, setShowJustification] = useState(false);

  // Durée du break-glass en minutes
  const BREAK_GLASS_DURATION = 30;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsActive(false);
            onDeactivate?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeRemaining, onDeactivate]);

  const handleActivate = async () => {
    if (!permissions.canBreakGlass) return;
    
    if (!justification.trim()) {
      setShowJustification(true);
      return;
    }

    try {
      // Logger l'activation du break-glass
      await auditActions.logCreate('USER', `break_glass_${Date.now()}`, {
        type: 'BREAK_GLASS_ACTIVATION',
        justification,
        timestamp: new Date().toISOString(),
        duration: BREAK_GLASS_DURATION
      }, 'UI');

      setIsActive(true);
      setTimeRemaining(BREAK_GLASS_DURATION * 60); // Convertir en secondes
      setJustification('');
      setShowJustification(false);
      onActivate?.();
    } catch (error) {
      console.error('Erreur lors de l\'activation du break-glass:', error);
    }
  };

  const handleDeactivate = async () => {
    try {
      // Logger la désactivation du break-glass
      await auditActions.logUpdate('USER', `break_glass_${Date.now()}`, {
        type: 'BREAK_GLASS_DEACTIVATION',
        timestamp: new Date().toISOString()
      }, 'UI');

      setIsActive(false);
      setTimeRemaining(0);
      onDeactivate?.();
    } catch (error) {
      console.error('Erreur lors de la désactivation du break-glass:', error);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = () => {
    if (isActive) {
      if (timeRemaining < 300) return 'text-red-600'; // Moins de 5 minutes
      if (timeRemaining < 900) return 'text-yellow-600'; // Moins de 15 minutes
      return 'text-green-600';
    }
    return 'text-gray-600';
  };

  const getStatusBgColor = () => {
    if (isActive) {
      if (timeRemaining < 300) return 'bg-red-50 border-red-200';
      if (timeRemaining < 900) return 'bg-yellow-50 border-yellow-200';
      return 'bg-green-50 border-green-200';
    }
    return 'bg-gray-50 border-gray-200';
  };

  if (!permissions.canBreakGlass) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Accès d'Urgence</h3>
        </div>
        
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-800">
              Vous n'avez pas l'autorisation d'utiliser l'accès d'urgence.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Shield className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Accès d'Urgence (Break-Glass)</h3>
      </div>

      {/* Explication */}
      <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <h4 className="text-sm font-semibold text-orange-900 mb-2">🚨 À quoi sert le Break-Glass ?</h4>
        <p className="text-sm text-orange-800 mb-2">
          Le <strong>Break-Glass</strong> est un <strong>accès d'urgence temporaire</strong> qui vous donne des droits étendus 
          pour résoudre un problème critique (ex: panne système, blocage urgent).
        </p>
        <ul className="text-xs text-orange-700 list-disc list-inside space-y-1">
          <li>Durée limitée : <strong>30 minutes</strong></li>
          <li>Justification obligatoire</li>
          <li>Toutes les actions sont <strong>auditées et tracées</strong></li>
          <li>Réservé aux administrateurs uniquement</li>
        </ul>
      </div>

      {/* Statut actuel */}
      <div className={`p-4 rounded-lg border ${getStatusBgColor()}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isActive ? (
              <Unlock className="h-5 w-5 text-green-600" />
            ) : (
              <Lock className="h-5 w-5 text-gray-600" />
            )}
            <span className={`font-medium ${getStatusColor()}`}>
              {isActive ? 'Accès d\'urgence ACTIF' : 'Accès d\'urgence INACTIF'}
            </span>
          </div>
          
          {isActive && (
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-mono">
                {formatTime(timeRemaining)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Justification */}
      {showJustification && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">
                Justification requise
              </span>
            </div>
            
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Décrivez la raison de l'activation de l'accès d'urgence..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
            
            <div className="flex space-x-2">
              <button
                onClick={handleActivate}
                disabled={!justification.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Activer l'Accès d'Urgence
              </button>
              <button
                onClick={() => setShowJustification(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {!isActive && !showJustification && (
        <div className="mt-4">
          <button
            onClick={() => setShowJustification(true)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <AlertTriangle className="h-4 w-4" />
            <span>Activer l'Accès d'Urgence</span>
          </button>
        </div>
      )}

      {isActive && (
        <div className="mt-4">
          <button
            onClick={handleDeactivate}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Lock className="h-4 w-4" />
            <span>Désactiver l'Accès d'Urgence</span>
          </button>
        </div>
      )}

      {/* Avertissements */}
      <div className="mt-4 space-y-2">
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-800">
              <strong>Attention:</strong> L'accès d'urgence est limité à {BREAK_GLASS_DURATION} minutes et est entièrement audité.
            </span>
          </div>
        </div>

        {isActive && timeRemaining < 300 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                <strong>Attention:</strong> L'accès d'urgence expire dans moins de 5 minutes.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BreakGlassAccess;
